import { CreateMetadataResponse } from '@vindr/services/apis/uploadAttachmentFile';
import keycloak from '@vindr/services/keycloak';
import { Omit } from '@vindr/utils/helpers/common.helper';
import { DEFAULT_URL_UPLOAD } from '@vindr/services/constants/common.constant';

export interface UploaderConfig {
  url?: Uploader['url'];
  headers?: Uploader['headers'];
  metadata?: Uploader['metadata'];
  type?: Uploader['type'];
  responseType?: '' | 'text' | 'arraybuffer' | 'blob' | 'document' | 'json';
}

const dashes = '--';
const crlf = '\r\n';

class Uploader {
  accessToken: string;
  end: (data: any) => void | Promise<any>;
  error: (error: any) => void;
  files: FormData;
  onProgress: (params: {
    loaded: number;
    total: number;
    status: number;
    percent: number;
    type: string;
  }) => void;
  url: string;
  xhr: XMLHttpRequest;
  headers: Record<string, unknown>;
  type: 'dicom' | 'attachment';
  metadata?: CreateMetadataResponse['data']['metadata'][number]['metadata'];
  responseType?: UploaderConfig['responseType'];

  constructor(config: UploaderConfig) {
    this.accessToken = keycloak.getPermissionToken();
    this.xhr = new XMLHttpRequest();
    this.url = config.url || `${DEFAULT_URL_UPLOAD}/pacs/rs/studies`;
    this.headers = config.headers || {};
    this.metadata = config.metadata;
    this.type = config.type || 'dicom';
    this.responseType = config.responseType || 'json';
  }

  // options(options: Options) {
  //   return this;
  // }

  send<T>(files: FormData): Promise<T> {
    if (!files) return;

    this.files = files;

    return this.upload(files);
  }

  abort() {
    this.xhr.abort();
  }

  upload<T>(files: FormData): Promise<T> {
    return new Promise((resolve, reject) => {
      if (!this.files) throw new Error('File is not set');
      const boundary = Math.random().toString().substring(2);
      const blob = this.type === 'dicom' ? this.formatDicomData({ files, boundary }) : files;

      const URL = this.url;
      const contentType = 'Content-Type';

      if (!this.accessToken) {
        const msg = 'Access token is not set';

        reject(this.error(new Error(msg)));
      }

      const progressListener = this.handleProgress.bind(this);
      const eventHandlers = {
        'error': progressListener,
        'abort': progressListener,
        'loadend': progressListener,
      };

      this.xhr.upload.addEventListener('progress', progressListener);
      this.setXHREvents('add', eventHandlers);

      this.xhr.open('POST', URL);

      this.setXHRHeaders({
        ...Omit(this.headers, [contentType]),
        ...(this.headers[contentType]
          ? { [contentType]: `${this.headers[contentType] || ''}; boundary=${boundary}` }
          : {}),
      });

      // this.xhr.responseType = this.responseType;

      this.xhr.onreadystatechange = () => {
        if (this.xhr.readyState === 4) {
          if (this.xhr.status === 200) {
            this.end(this.xhr.response as T);
            resolve(this.xhr.response as T);
          } else {
            reject(this.error(this.xhr.responseText));
          }
        }
      };

      this.xhr.onerror = (error) => {
        reject(this.error(error));
      };

      this.xhr.onabort = () => {
        reject(this.error(new Error('Upload canceled by user')));
      };

      this.xhr.onloadend = () => {
        this.xhr.upload.removeEventListener('progress', progressListener);
        this.setXHREvents('remove', eventHandlers);
      };

      this.xhr.send(blob);
    });
  }

  private formatDicomData({ files, boundary }: { files: FormData; boundary: string }) {
    const mimeType = 'application/dicom';

    const blobFiles: Array<string | File> = [
      dashes + boundary + crlf,
      `Content-Type: multipart/related; boundary="${boundary}"${crlf}`,
    ];

    files.forEach((file: File) => {
      const postDataStart =
        crlf +
        dashes +
        boundary +
        crlf +
        `Content-Disposition: form-data; name="${encodeURIComponent(file.name)}"; filename="` +
        encodeURIComponent(file.name) +
        '"' +
        crlf +
        'Content-Type: ' +
        mimeType +
        crlf +
        crlf;

      blobFiles.push(postDataStart, file);
    });
    const postDataEnd = crlf + dashes + boundary + dashes;

    blobFiles.push(postDataEnd);

    return new Blob(blobFiles, { type: 'multipart/related' });
  }

  /*   private formatAttachmentData({ files, boundary, metadata }) {
    const mimeType = 'application/dicom+json';

    const blobFiles = [];

    files.forEach((file: File) => {
      const jsonData =
        dashes +
        boundary +
        crlf +
        `Content-Type: ${mimeType}` +
        crlf +
        crlf +
        JSON.stringify([Omit(metadata, ['required', 'enum', 'multi'])]) +
        crlf;
      const postDataStart =
        jsonData +
        dashes +
        boundary +
        crlf +
        `Content-Type: ${file.type}` +
        crlf +
        `Content-Location: file/${file.name}` +
        crlf +
        crlf;

      blobFiles.push(postDataStart, file);
    });
    const postDataEnd = crlf + dashes + boundary + dashes;

    blobFiles.push(postDataEnd);

    return new Blob(blobFiles);
  } */

  handleProgress(event: ProgressEvent<any>) {
    const { loaded, total, type } = event;
    const status = event?.target?.status;
    const percent = Math.round((loaded / total) * 100 * 100) / 100;

    this.onProgress({ loaded, total, status, percent, type });
  }

  setXHRHeaders(obj: unknown) {
    Object.keys(obj).forEach((key) => {
      this.xhr.setRequestHeader(key, obj[key]);
    });
  }

  setXHREvents(type: 'add' | 'remove', obj: unknown) {
    const func = type === 'add' ? this.xhr.addEventListener : this.xhr.removeEventListener;

    Object.keys(obj).forEach((key) => {
      func.call(this.xhr, key, obj[key]);
    });
  }
}

export default Uploader;
